  import { PayloadAction, createSlice } from "@reduxjs/toolkit";
  import { Step, StepSlugsEnum } from "../../../v2/core/common_types";
  import { Color, Option } from "../../../v2/models/door_configuration";
  import {
    AspectPergola,
    ChiusuraType,
    DimensionPergola,
    FarettoType,
    Fioriera,
    Fioriere,
    IlluminationType,
    LatoIlluminazioneFaretto,
    LatoType,
    MuroPergola,
    PergolaConfiguration,
    Pilastri,
    Tubolare,
  } from "../../../v2/models/pergola_configuration";
import { LatoInstallation } from "../../../v2/routes/configurations/pergola/builder/components/forms/Pergola_type";
  import { LatoIlluminazioneLed, LatoPergola } from "../../../v2/routes/configurations/pergola/builder/components/pergola-draw/Pergola";

  export type PergolaConfigurationBuilderState = {
    configuration: PergolaConfiguration | null;
    steps: Step[];
    options: Option[];
    colors: Color[];
    lati: LatoPergola[];
    led: LatoIlluminazioneLed[];
    muri: MuroPergola[];
    tubolari: Tubolare[];
    pilastri: Pilastri[];
    fioriere: Fioriere[];
    faretti: LatoIlluminazioneFaretto,
    moduliProfondita: number,
    moduliLarghezza: number
  };

  const initialState: PergolaConfigurationBuilderState = {
    configuration: null,
    steps: [],
    options: [],
    colors: [],
    lati: [],
    led: [],
    muri: [],
    tubolari: [],
    fioriere: [],
    pilastri: [],
    faretti: { numero: 0, tipo: FarettoType.nessuno },
    moduliProfondita: 1,
    moduliLarghezza: 1
  };

  const reducers = {
    clearStorage(state: PergolaConfigurationBuilderState) {
      state.configuration = initialState.configuration;
      state.steps = initialState.steps;
    },

    setConfiguration(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{ configuration: PergolaConfiguration; options: Option[]; colors: Color[] }>
    ) {
      state.configuration = action.payload.configuration;
      state.options = action.payload.options;
      state.colors = action.payload.colors;
    },

    setStepActive(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{ stepSlug: StepSlugsEnum; isActive: boolean }>
    ) {
      state.steps = state.steps.map((s) =>
        s.slug === action.payload.stepSlug
          ? { ...s, isActive: action.payload.isActive }
          : { ...s, isActive: false }
      );
    },

    toggleLed(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{ direzione: "Retro" | "Fronte" | "Destra" | "Sinistra"; tipo: IlluminationType }>
    ) {
      const { direzione, tipo } = action.payload;
      const index = state.led.findIndex((led) => led.direzione === direzione);

      if (tipo === IlluminationType.nessuno) {
        if (index > -1) state.led.splice(index, 1);
      } else {
        if (index > -1) {
          state.led[index].tipo = tipo;
        } else {
          state.led.push({ direzione, tipo });
        }
      }
    },
    setFaretti(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<LatoIlluminazioneFaretto>
    ) {
      state.faretti = action.payload;
    },
    resetFaretti(state: PergolaConfigurationBuilderState) {
      state.faretti = { numero: 0, tipo: FarettoType.nessuno };
    },
    setLeds(state: PergolaConfigurationBuilderState, action: PayloadAction<LatoIlluminazioneLed[]>) {
      state.led = action.payload;
    },
    setModuliProfondita(state: PergolaConfigurationBuilderState, action: PayloadAction<number>) {
      console.log("Inserto modulo Prof")
      state.moduliProfondita = action.payload;
    },
    setModuliLarghezza(state: PergolaConfigurationBuilderState, action: PayloadAction<number>) {
      state.moduliLarghezza = action.payload;
    },
    setTubolari(state: PergolaConfigurationBuilderState, action: PayloadAction<Tubolare[]>) {
      state.tubolari = action.payload;
    },
    setMuro(state: PergolaConfigurationBuilderState, action: PayloadAction<MuroPergola[]>) {
      const nuoviMuri = action.payload;
      const esistenti = state.muri.filter(
        (muro) => !nuoviMuri.some((n) => n.direzione === muro.direzione)
      );

      state.muri = [...esistenti, ...nuoviMuri];
    },
    setMuroSingolo(state: PergolaConfigurationBuilderState, action: PayloadAction<MuroPergola>) {
      const { direzione, altezza } = action.payload;
      state.muri = state.muri.filter((m) => m.direzione !== direzione);
      if (altezza > 0) {
        state.muri.push({ direzione, altezza });
      }
    },
    setTubolareSingolo(state: PergolaConfigurationBuilderState, action: PayloadAction<Tubolare>) {
      const { direzione, numero } = action.payload;
      state.tubolari = state.tubolari.filter((t) => t.direzione !== direzione);

      if (numero > 0) {
        console.log("OK aggiunto",direzione)
        state.tubolari.push({ direzione, numero });
      }
    },
    setStepComplete(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{ stepSlug: StepSlugsEnum; isComplete: boolean }>
    ) {
      state.steps = state.steps.map((s) =>
        s.slug === action.payload.stepSlug ? { ...s, isComplete: action.payload.isComplete } : s
      );
    },
    setPilastri(state: PergolaConfigurationBuilderState, action: PayloadAction<Pilastri[]>) {
      state.pilastri = action.payload.filter(
        (p): p is Pilastri =>
          typeof p.altezza === "number" &&
          p.altezza > 0 &&
          ["H1", "H2", "H3", "H4"].includes(p.direzione)
      );
    },
    setPilastroSingolo(state: PergolaConfigurationBuilderState, action: PayloadAction<Pilastri>) {
      const { direzione, altezza } = action.payload;
      state.pilastri = state.pilastri.filter((p) => p.direzione !== direzione);
      if (altezza > 0) {
        state.pilastri.push({ direzione, altezza });
      }
    },
    setStepVisible(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{ stepSlug: StepSlugsEnum; isVisible: boolean }>
    ) {
      state.steps = state.steps.map((s) =>
        s.slug === action.payload.stepSlug ? { ...s, isVisible: action.payload.isVisible } : s
      );
    },

    setQuantita(state: PergolaConfigurationBuilderState, action: PayloadAction<number>) {
      if (!state.configuration) return;
      state.configuration.dimensioni.quantita = action.payload;
    },

    setAltezza(state: PergolaConfigurationBuilderState, action: PayloadAction<number>) {
      if (!state.configuration) return;
      state.configuration.dimensioni.altezza = action.payload;
    },

    setProfondita(state: PergolaConfigurationBuilderState, action: PayloadAction<number>) {
      if (!state.configuration) return;
      state.configuration.dimensioni.profondita = action.payload;
    },

    setLarghezza(state: PergolaConfigurationBuilderState, action: PayloadAction<number>) {
      if (!state.configuration) return;
      state.configuration.dimensioni.larghezza = action.payload;
    },

    setPergolaDimension(state: PergolaConfigurationBuilderState, action: PayloadAction<DimensionPergola>) {
      if (!state.configuration) return;
      console.log("DIMENSIONI AFFIA=SID",action.payload)
      state.configuration.dimensioni = { ...state.configuration.dimensioni, ...action.payload };
    },

    setPergolaAspect(state: PergolaConfigurationBuilderState, action: PayloadAction<AspectPergola>) {
      if (!state.configuration) return;
      state.configuration.aspetto = { ...state.configuration.aspetto, ...action.payload };
    },

    setFiorieraSingola(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<Fioriere>
    ) {
      const { direzione, tipo } = action.payload;
      console.log("SET FIORIERA",direzione, tipo);
      state.fioriere = state.fioriere.filter(f => f.direzione !== direzione);
      if ( tipo !== null && tipo !== undefined) {
        state.fioriere.push({ direzione, tipo });
      }
    },

    toggleFioriera(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{
        direzione: string;
        tipo: Fioriera;
      }>
    ) {
      const { direzione, tipo } = action.payload;
      const index = state.fioriere.findIndex(f => f.direzione === direzione);

      if (tipo === null || tipo === undefined) {
        if (index > -1) {
          state.fioriere.splice(index, 1);
        }
      } else {
        if (index > -1) {
          state.fioriere[index].tipo = tipo;
        } else {
          state.fioriere.push({ direzione, tipo });
        }
      }
    },

    setFioriere(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<Fioriere[]>
    ) {
      state.fioriere = action.payload.filter(f => 
        f.tipo !== null && f.tipo !== undefined
      );
    },

    removeFioriera(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{ direzione: string }>
    ) {
      const { direzione } = action.payload;
      state.fioriere = state.fioriere.filter(f => f.direzione !== direzione);
      console.log("STATE AFTER REMOVE",state.fioriere)
    },
    removeFiorieraTipo(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{ tipo: Fioriera }>
    ) {
      const { tipo } = action.payload;
      state.fioriere = state.fioriere.filter(f => f.tipo !== tipo);
      console.log("STATE AFTER REMOVE",state.fioriere)
    },
    clearAllFioriere(state: PergolaConfigurationBuilderState) {
      state.fioriere = [];
    },
    setPergolaSide(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{ 
      direzione: "Retro" | "Fronte" | "Destra" | "Sinistra";
      segmento: number; 
      tipoLato: LatoType 
      }>
      ) {
      if (!state.configuration) return;

      const { direzione, segmento, tipoLato } = action.payload;
      const index = state.lati.findIndex(
      (l) => l.direzione === direzione && l.segmento === segmento
      );

      if (index !== -1) {
        state.lati[index].tipoLato = tipoLato;
      } else {
        state.lati.push({ direzione, segmento, tipoLato });
      }
    },
    removePergolaSide(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{
        direzione: "Retro" | "Fronte" | "Destra" | "Sinistra";
      }>
    ) {
      if (!state.configuration) return;

      const { direzione } = action.payload;

      state.lati = state.lati.filter(
        lato => !(lato.direzione === direzione )
      );
    },
    removePergolaSideSingleSegment(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{
        direzione: "Retro" | "Fronte" | "Destra" | "Sinistra";
        segmento: number;
      }>
    ) {
      const { direzione, segmento } = action.payload;
      state.lati = state.lati.filter(
        (lato) => !(lato.direzione === direzione && lato.segmento === segmento)
      );
    },
    removePergolaSideSegmentsFrom(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{
        direzione: "Retro" | "Fronte" | "Destra" | "Sinistra";
        fromSegmento: number;
      }>
    ) {
      const { direzione, fromSegmento } = action.payload;

      state.lati = state.lati.filter(
        (lato) => !(lato.direzione === direzione && lato.segmento >= fromSegmento)
      );
    },
    setPergolaSides(
      state: PergolaConfigurationBuilderState,
      action: PayloadAction<{
        sides: { direzione: "Retro" | "Fronte" | "Destra" | "Sinistra"; segmento: number; tipoLato: LatoType }[];
      }>
    ) {
      if (!state.configuration) return;
      action.payload.sides.forEach(({ direzione, segmento, tipoLato }) => {
        const index = state.lati.findIndex((l) => l.direzione === direzione && l.segmento === segmento);
        if (index !== -1) {
          state.lati[index] = { ...state.lati[index], tipoLato, segmento };
        } else {
          state.lati.push({ direzione, segmento, tipoLato });
        }
      });
    },
    resetBuilder(state: PergolaConfigurationBuilderState) {
      state.configuration = null;
      state.options = [];
      state.colors = [];
      state.lati = [];
      state.led = [];
      state.muri = [];
      state.tubolari = [];
      state.pilastri = [];
      state.steps = state.steps.map(s => ({ ...s, isVisible: false, isActive: false, isComplete: false }));
    },
    resetSteps(state: PergolaConfigurationBuilderState) {
        let steps = state.steps;
        steps.forEach(s => s.isComplete = false);
        state.steps = steps;
    },

    resetSides(state: PergolaConfigurationBuilderState) {
      state.lati = [];
    },

    setSteps(state: PergolaConfigurationBuilderState, action: PayloadAction<Step[]>) {
      state.steps = action.payload;
    },
  };

  const slice = createSlice({
    name: "pergola_configuration_builder",
    initialState,
    reducers,
  });

  const { actions, reducer } = slice;

  export const {
    setTubolareSingolo,
    setMuro,
    setMuroSingolo,
    setQuantita,
    setAltezza,
    setProfondita,
    setLarghezza,
    setPergolaDimension,
    setPergolaSide,
    setPergolaAspect,
    toggleLed,
    setLeds,
    setStepActive,
    setStepComplete,
    setStepVisible,
    setConfiguration,
    clearStorage,
    resetSteps,
    resetSides,
    setSteps,
    setModuliProfondita,
    setModuliLarghezza,
    setPilastroSingolo,
    resetBuilder,
    setTubolari,
    setPergolaSides,
    removePergolaSide,
    setFiorieraSingola,
    toggleFioriera,
    setFaretti,
    resetFaretti,
    setFioriere,
    removeFioriera,
    clearAllFioriere,
    removeFiorieraTipo,
    setPilastri,
    removePergolaSideSegmentsFrom,
    removePergolaSideSingleSegment
  } = actions;

  export function usePergolaConfigurationBuilderActions() {
    return {
      setTubolareSingolo,
      setMuro,
      setMuroSingolo,
      setQuantita,
      setAltezza,
      setProfondita,
      setLarghezza,
      setPergolaDimension,
      setPergolaSide,
      setPergolaAspect,
      toggleLed,
      setLeds,
      setTubolari,
      setStepActive,
      setStepComplete,
      setFaretti,
      resetFaretti,
      setStepVisible,
      setConfiguration,
      setModuliProfondita,
      setModuliLarghezza,
      clearStorage,
      resetSteps,
      resetSides,
      resetBuilder,
      setSteps,
      setPilastroSingolo,
      setPergolaSides,
      removePergolaSide,
      setFiorieraSingola,
      toggleFioriera,
      setFioriere,
      removeFioriera,
      clearAllFioriere,
      removeFiorieraTipo,
      setPilastri,
      removePergolaSideSegmentsFrom,
      removePergolaSideSingleSegment
    };
  }

  export default reducer;